import { type ReactNode, useMemo, useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { Sparkline } from '../../charts/Sparkline';
import { Drawer } from '../../components/data';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import { useHeldResource } from '../../lib/hooks';

/**
 * One client's service performance, live from Autotask: open tickets, tickets
 * outside their resolution SLA, average resolution time and CSAT for a week, a
 * month or a whole year, against the one before; the flow of tickets opened and
 * resolved, day by day — month by month across a year; and four health bars.
 *
 * ONE READ, EVERYTHING COUNTED HERE. The API returns the client's raw tickets
 * and survey responses (GET /am/clients/:id/autotask/service-overview) and
 * every figure below is counted from them in the browser. So stepping between
 * weeks, or narrowing to a board, a category or parent tickets only, asks
 * Autotask nothing — and every figure opens the tickets it was counted from,
 * through the same functions that counted it, so the number and its list can
 * never disagree.
 *
 * The reading is kept, like the Service Delivery dials: it is shown however
 * old until Refresh, and the badge says when it was taken.
 */

interface Ticket {
  id: number;
  ticketNumber: string;
  title: string;
  status: string;
  complete: boolean;
  priority: string;
  boardId: number | null;
  board: string;
  categoryId: number | null;
  category: string;
  issueType: string;
  /** Attached to a problem ticket — an incident of it, not a parent. */
  isChild: boolean;
  assignedTo: string | null;
  created: string | null;
  completed: string | null;
  hasSla: boolean;
  firstResponse: string | null;
  firstResponseDue: string | null;
  resolved: string | null;
  resolvedDue: string | null;
}

interface Survey {
  id: number;
  ticketId: number | null;
  /** Autotask's survey rating, 1 to 5. */
  rating: number | null;
  completed: string | null;
}

interface Overview {
  linked: boolean;
  companyName: string | null;
  from?: string;
  tickets?: Ticket[];
  surveys?: Survey[];
  fetchedUtc?: string;
}

type Grouping = 'week' | 'month' | 'year';
interface Period {
  start: Date;
  end: Date;
}

/* How many periods the sparklines and the stepper hold; one more, the oldest,
   is read but never shown — it is only there so the first of them still has a
   "prior" to compare with. Years are the short one: the Autotask read reaches
   back three calendar years, so two are shown and the third is that prior. */
const SHOWN: Record<Grouping, number> = { week: 8, month: 8, year: 2 };
const SCALE_MAX = 5;

const ms = (iso: string | null) => (iso ? Date.parse(iso) : NaN);
const within = (iso: string | null, p: Period) => {
  const t = ms(iso);
  return t >= p.start.getTime() && t < p.end.getTime();
};

/** Monday-start weeks, calendar months and calendar years, in this browser — as Reports and Service Delivery count them. */
function periodsEndingNow(grouping: Grouping): Period[] {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (grouping === 'week') start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  else if (grouping === 'month') start.setDate(1);
  else start.setMonth(0, 1);
  const out: Period[] = [];
  for (let back = SHOWN[grouping]; back >= 0; back -= 1) {
    const s = new Date(start);
    const e = new Date(start);
    if (grouping === 'week') {
      s.setDate(s.getDate() - 7 * back);
      e.setDate(e.getDate() - 7 * back + 7);
    } else if (grouping === 'month') {
      s.setMonth(s.getMonth() - back);
      e.setMonth(e.getMonth() - back + 1);
    } else {
      s.setFullYear(s.getFullYear() - back);
      e.setFullYear(e.getFullYear() - back + 1);
    }
    out.push({ start: s, end: e });
  }
  return out;
}

const dayFmt = (d: Date, withYear = false) =>
  d.toLocaleDateString([], { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });

function periodLabel(p: Period, grouping: Grouping) {
  if (grouping === 'year') return String(p.start.getFullYear());
  if (grouping === 'month') return p.start.toLocaleDateString([], { month: 'long', year: 'numeric' });
  const last = new Date(p.end);
  last.setDate(last.getDate() - 1);
  return `${dayFmt(p.start)} – ${dayFmt(last, true)}`;
}

/* ------------------------------------------------------------- the counting */

/** Open at the instant: raised before it, and not completed before it. */
const openAt = (tickets: Ticket[], at: number) =>
  tickets.filter((t) => ms(t.created) < at && !(t.complete && (!t.completed || ms(t.completed) < at)));
const completedIn = (tickets: Ticket[], p: Period) => tickets.filter((t) => t.complete && within(t.completed, p));
/** Missed when resolved after the due time, or never stamped resolved at all. */
const missedResolution = (t: Ticket) => !t.resolved || ms(t.resolved) > ms(t.resolvedDue);
const outsideSla = (tickets: Ticket[], p: Period) =>
  completedIn(tickets, p).filter((t) => t.resolvedDue && missedResolution(t));
const hoursToResolve = (t: Ticket) => (ms(t.completed) - ms(t.created)) / 3_600_000;
const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const surveysIn = (surveys: Survey[], p: Period) => surveys.filter((s) => s.rating !== null && within(s.completed, p));
/** The plain 1–5 survey average, read as Service Delivery reads it — out of 5. */
const csatScore = (ss: Survey[]) => mean(ss.map((s) => s.rating as number));

interface Metrics {
  open: Ticket[];
  outside: Ticket[];
  completed: Ticket[];
  avgHours: number | null;
  surveys: Survey[];
  csat: number | null;
}
function metricsFor(tickets: Ticket[], surveys: Survey[], p: Period): Metrics {
  const completed = completedIn(tickets, p);
  const ss = surveysIn(surveys, p);
  return {
    open: openAt(tickets, Math.min(p.end.getTime(), Date.now())),
    outside: outsideSla(tickets, p),
    completed,
    avgHours: mean(completed.map(hoursToResolve).filter((h) => Number.isFinite(h) && h >= 0)),
    surveys: ss,
    csat: csatScore(ss),
  };
}

/* ------------------------------------------------------------------ the page */

interface Drill {
  title: string;
  subtitle: string;
  tickets: Ticket[];
  /** Survey scores to show beside the tickets, for the CSAT drill. */
  scores?: Map<number, number>;
}

export function AmServicePerformance({ clientId, clientName }: { clientId: string; clientName: string }) {
  const live = useHeldResource<Overview>(`/am/clients/${clientId}/autotask/service-overview`);
  const [grouping, setGrouping] = useState<Grouping>('week');
  /* Which of the shown periods: the last complete one by default, so a Monday
     morning does not open on a week that is an hour old. */
  const [pick, setPick] = useState(SHOWN.week - 2);
  const [board, setBoard] = useState<string>('all');
  const [category, setCategory] = useState<string>('all');
  const [parentsOnly, setParentsOnly] = useState(true);
  const [drill, setDrill] = useState<Drill | null>(null);

  const data = live.data;
  const all = useMemo(() => data?.tickets ?? [], [data]);

  const tickets = useMemo(
    () =>
      all.filter(
        (t) =>
          (board === 'all' || String(t.boardId) === board) &&
          (category === 'all' || String(t.categoryId) === category) &&
          (!parentsOnly || !t.isChild),
      ),
    [all, board, category, parentsOnly],
  );
  /* A survey counts where its ticket does. With nothing narrowed, one whose
     ticket fell outside the read still counts — it is still the client's. */
  const surveys = useMemo(() => {
    const ss = data?.surveys ?? [];
    const narrowed = board !== 'all' || category !== 'all' || parentsOnly;
    if (!narrowed) return ss;
    const ids = new Set(tickets.map((t) => t.id));
    return ss.filter((s) => s.ticketId !== null && ids.has(s.ticketId));
  }, [data, tickets, board, category, parentsOnly]);

  const periods = useMemo(() => periodsEndingNow(grouping), [grouping]);
  const metrics = useMemo(() => periods.map((p) => metricsFor(tickets, surveys, p)), [periods, tickets, surveys]);

  const boards = useMemo(() => options(all, 'boardId', 'board'), [all]);
  const categories = useMemo(() => options(all, 'categoryId', 'category'), [all]);

  if (live.loading) return <Loading label="Reading tickets from Autotask" />;
  if (live.error && !data) return <ErrorBanner message={live.error} />;
  if (!data?.linked) {
    return (
      <Empty title="Not found in Autotask">
        <p className="muted">
          {clientName} could not be found in Autotask under its name here, so there are no tickets to show. Importing it
          from Autotask (Account Management › Import from Autotask) matches the names.
        </p>
      </Empty>
    );
  }

  const idx = pick + 1; /* metrics[0] is the hidden comparison period */
  const p = periods[idx]!;
  const m = metrics[idx]!;
  const prev = metrics[idx - 1]!;
  const isCurrent = idx === periods.length - 1;
  const label = periodLabel(p, grouping);
  const unit = grouping;
  const shownCount = SHOWN[grouping];
  const groupWord = grouping === 'week' ? 'Weekly' : grouping === 'month' ? 'Monthly' : 'Yearly';
  const shown = metrics.slice(1);

  /* The health bars: what share of the period's completed tickets met each bar. */
  const surveyed = new Set((data.surveys ?? []).map((s) => s.ticketId));
  const withRes = m.completed.filter((t) => t.resolvedDue);
  const withFirst = m.completed.filter((t) => t.firstResponseDue);
  const firstMissed = (t: Ticket) => !t.firstResponse || ms(t.firstResponse) > ms(t.firstResponseDue);
  const fullData = (t: Ticket) => t.hasSla && t.categoryId !== null && !!t.assignedTo;
  const health = [
    {
      label: 'Resolution SLA',
      value: share(withRes.length - withRes.filter(missedResolution).length, withRes.length),
      count: withRes.length,
      of: `${withRes.length} completed with a resolution SLA`,
      misses: withRes.filter(missedResolution),
      missWord: 'missed the resolution SLA',
    },
    {
      label: 'First response SLA',
      value: share(withFirst.length - withFirst.filter(firstMissed).length, withFirst.length),
      count: withFirst.length,
      of: `${withFirst.length} completed with a first-response SLA`,
      misses: withFirst.filter(firstMissed),
      missWord: 'missed the first-response SLA',
    },
    {
      label: 'CSAT coverage',
      value: share(m.completed.filter((t) => surveyed.has(t.id)).length, m.completed.length),
      count: m.completed.length,
      of: `${m.completed.length} completed, share with a survey response`,
      misses: m.completed.filter((t) => !surveyed.has(t.id)),
      missWord: 'have no survey response',
    },
    {
      label: 'PSA data coverage',
      value: share(m.completed.filter(fullData).length, m.completed.length),
      count: m.completed.length,
      of: `${m.completed.length} completed, share with an SLA, a category and an assignee`,
      misses: m.completed.filter((t) => !fullData(t)),
      missWord: 'are missing an SLA, a category or an assignee',
    },
  ];
  const judged = health.filter((h) => h.value !== null).map((h) => h.value as number);
  const worst = judged.length ? Math.min(...judged) : null;
  const verdict =
    worst === null ? null : worst >= 90 ? { word: 'Healthy', tone: 'good' } : worst >= 75 ? { word: 'Watch', tone: 'warning' } : { word: 'Needs attention', tone: 'critical' };

  /* The flow chart: one column per day of the period — or, across a year,
     one per month, since 365 columns say nothing. */
  const byMonth = grouping === 'year';
  const columnWord = byMonth ? 'month' : 'day';
  const days: { key: string; day: string; opened: number; resolved: number; start: number; end: number }[] = [];
  for (let d = new Date(p.start); d < p.end; byMonth ? d.setMonth(d.getMonth() + 1) : d.setDate(d.getDate() + 1)) {
    const col: Period = {
      start: new Date(d),
      end: byMonth
        ? new Date(d.getFullYear(), d.getMonth() + 1, 1)
        : new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1),
    };
    days.push({
      key: d.toISOString(),
      day: byMonth
        ? col.start.toLocaleDateString([], { month: 'short' })
        : grouping === 'week'
          ? d.toLocaleDateString([], { weekday: 'short' })
          : String(d.getDate()),
      opened: tickets.filter((t) => within(t.created, col)).length,
      resolved: tickets.filter((t) => t.complete && within(t.completed, col)).length,
      start: col.start.getTime(),
      end: col.end.getTime(),
    });
  }
  const openDay = (row: (typeof days)[number]) => {
    const col: Period = { start: new Date(row.start), end: new Date(row.end) };
    const opened = tickets.filter((t) => within(t.created, col));
    const resolved = tickets.filter((t) => t.complete && within(t.completed, col));
    const list = [...new Set([...opened, ...resolved])];
    setDrill({
      title: byMonth
        ? `Tickets in ${col.start.toLocaleDateString([], { month: 'long', year: 'numeric' })}`
        : `Tickets on ${col.start.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}`,
      subtitle: `${opened.length} opened · ${resolved.length} resolved`,
      tickets: list,
    });
  };

  const scores = new Map(m.surveys.filter((s) => s.ticketId !== null).map((s) => [s.ticketId as number, s.rating as number]));
  const surveyTickets = m.surveys
    .map((s) => all.find((t) => t.id === s.ticketId))
    .filter((t): t is Ticket => !!t);

  return (
    <section className="am-sp">
      <div className="am-sp-head">
        <div>
          <h2>Service performance overview</h2>
          <p className="muted">
            {groupWord} performance across {data.companyName ?? clientName}'s Autotask tickets
          </p>
        </div>
        <div className="am-sp-read">
          {live.error ? (
            <span className="badge badge-critical">{live.error}</span>
          ) : (
            <span className="badge badge-good">Read from Autotask {data.fetchedUtc ? readWhen(data.fetchedUtc) : ''}</span>
          )}
          <button type="button" className="link-button xs" disabled={live.refreshing} onClick={() => live.refresh()}>
            {live.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="am-sp-filters">
        <Chip label="Time grouping">
          <select
            value={grouping}
            onChange={(e) => {
              const next = e.target.value as Grouping;
              setGrouping(next);
              setPick(SHOWN[next] - 2);
            }}
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </Chip>
        <Chip label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">All</option>
            {categories.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </Chip>
        <Chip label="Board">
          <select value={board} onChange={(e) => setBoard(e.target.value)}>
            <option value="all">All boards</option>
            {boards.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </Chip>
        <Chip label="Tickets">
          <select value={parentsOnly ? 'parent' : 'all'} onChange={(e) => setParentsOnly(e.target.value === 'parent')}>
            <option value="parent">Parent only</option>
            <option value="all">All tickets</option>
          </select>
        </Chip>
        <div className="am-sp-stepper" role="group" aria-label={`Choose the ${unit}`}>
          <button type="button" className="btn btn-ghost btn-sm" disabled={pick === 0} onClick={() => setPick(pick - 1)} aria-label={`Previous ${unit}`}>
            ‹
          </button>
          <span className="xs strong">
            {label}
            {isCurrent && <span className="muted"> · so far</span>}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={pick === shownCount - 1}
            onClick={() => setPick(pick + 1)}
            aria-label={`Next ${unit}`}
          >
            ›
          </button>
        </div>
      </div>

      <div className="am-sp-kpis">
        <Kpi
          title="Open tickets"
          value={fmt(m.open.length)}
          period={isCurrent ? 'now' : `at the end of ${label}`}
          change={change(m.open.length, prev.open.length)}
          prior={`${fmt(prev.open.length)} prior ${unit}`}
          better="lower"
          trend={shown.map((x) => x.open.length)}
          onOpen={() =>
            setDrill({
              title: 'Open tickets',
              subtitle: `${m.open.length} open ${isCurrent ? 'now' : `at the end of ${label}`} · oldest first`,
              tickets: [...m.open].sort((a, b) => ms(a.created) - ms(b.created)),
            })
          }
        />
        <Kpi
          title="Outside resolution SLA"
          value={fmt(m.outside.length)}
          period={label}
          change={change(m.outside.length, prev.outside.length)}
          prior={`${fmt(prev.outside.length)} prior ${unit}`}
          better="lower"
          trend={shown.map((x) => x.outside.length)}
          onOpen={() =>
            setDrill({
              title: 'Completed outside the resolution SLA',
              subtitle: `${m.outside.length} completed in ${label} that were resolved after their due time, or never marked resolved`,
              tickets: m.outside,
            })
          }
        />
        <Kpi
          title="Average resolution time"
          value={m.avgHours === null ? '—' : `${m.avgHours.toFixed(1)} hrs`}
          period={label}
          change={m.avgHours === null || prev.avgHours === null ? null : change(m.avgHours, prev.avgHours)}
          prior={prev.avgHours === null ? `none prior ${unit}` : `${prev.avgHours.toFixed(1)} hrs prior ${unit}`}
          better="lower"
          trend={shown.map((x) => x.avgHours)}
          onOpen={() =>
            setDrill({
              title: 'Resolution time',
              subtitle: `${m.completed.length} completed in ${label} · created to completed, longest first`,
              tickets: [...m.completed].sort((a, b) => hoursToResolve(b) - hoursToResolve(a)),
            })
          }
        />
        <Kpi
          title="Average CSAT score"
          value={m.csat === null ? '—' : m.csat.toFixed(2)}
          period={`out of ${SCALE_MAX} · ${label}${m.surveys.length ? ` · ${m.surveys.length} survey${m.surveys.length === 1 ? '' : 's'}` : ''}`}
          change={m.csat === null || prev.csat === null ? null : change(m.csat, prev.csat)}
          prior={prev.csat === null ? `no surveys prior ${unit}` : `${prev.csat.toFixed(2)} prior ${unit}`}
          better="higher"
          trend={shown.map((x) => x.csat)}
          note={`Autotask's survey rating, averaged, out of ${SCALE_MAX}`}
          onOpen={() =>
            setDrill({
              title: 'Survey responses',
              subtitle: `${m.surveys.length} completed in ${label} · Autotask's 1–5 rating`,
              tickets: surveyTickets,
              scores,
            })
          }
        />
      </div>

      <div className="am-sp-lower">
        <div className="card am-sp-flow">
          <div className="card-head">
            <div className="grow">
              <h3>{groupWord} ticket flow</h3>
              <p className="sub">
                Opened versus resolved tickets, by {columnWord} · click a {columnWord} for its tickets
              </p>
            </div>
          </div>
          <BarChart
            data={days}
            xKey="day"
            series={[
              { key: 'opened', label: 'Opened', color: 'var(--series-1)' },
              { key: 'resolved', label: 'Resolved', color: 'var(--series-2)' },
            ]}
            height={240}
            onSelect={(row) => openDay(row)}
          />
        </div>

        <div className="card am-sp-health">
          <div className="card-head">
            <div className="grow">
              <h3>Dashboard health</h3>
              <p className="sub">Coverage and service quality · {label}</p>
            </div>
            {verdict && <span className={`badge badge-${verdict.tone}`}>{verdict.word}</span>}
          </div>
          {health.map((h) => (
            <button
              type="button"
              key={h.label}
              className="am-sp-bar"
              disabled={!h.misses.length}
              title={h.of}
              onClick={() =>
                setDrill({
                  title: h.label,
                  subtitle: `${h.misses.length} of ${h.count} ${h.missWord} · ${label}`,
                  tickets: h.misses,
                })
              }
            >
              <span className="am-sp-bar-row">
                <span>{h.label}</span>
                <span className="strong">{h.value === null ? '—' : `${h.value.toFixed(1)}%`}</span>
              </span>
              <span className="am-sp-track">
                <span className={`am-sp-fill ${tone(h.value)}`} style={{ width: `${h.value ?? 0}%` }} />
              </span>
              {h.misses.length > 0 && <span className="xs muted">{h.misses.length} {h.missWord} →</span>}
            </button>
          ))}
        </div>
      </div>

      <Drawer open={drill !== null} onClose={() => setDrill(null)} wide title={drill?.title ?? ''} subtitle={drill?.subtitle}>
        {drill && <TicketTable tickets={drill.tickets} scores={drill.scores} />}
      </Drawer>
    </section>
  );
}

/* --------------------------------------------------------------- the pieces */

function Chip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="am-sp-chip">
      <span className="muted">{label}:</span>
      {children}
    </label>
  );
}

interface Change {
  pct: number | null;
  dir: 'up' | 'down' | 'flat';
}
const change = (now: number, before: number): Change => {
  if (now === before) return { pct: 0, dir: 'flat' };
  return { pct: before === 0 ? null : (Math.abs(now - before) / before) * 100, dir: now > before ? 'up' : 'down' };
};

function Kpi({
  title,
  value,
  period,
  change: ch,
  prior,
  better,
  trend,
  note,
  onOpen,
}: {
  title: string;
  value: string;
  period: string;
  change: Change | null;
  prior: string;
  better: 'higher' | 'lower';
  trend: Array<number | null>;
  note?: string;
  onOpen: () => void;
}) {
  /* Good or bad by which way is better for THIS figure — an arrow up is good
     news for CSAT and bad news for open tickets. Said in words too, never by
     colour alone. */
  const good = ch === null || ch.dir === 'flat' ? null : (ch.dir === 'up') === (better === 'higher');
  const toneClass = good === null ? '' : good ? 'is-good' : 'is-bad';
  return (
    <button type="button" className="card am-sp-kpi" onClick={onOpen} title={note}>
      <span className="am-sp-kpi-title">{title}</span>
      <span className="am-sp-kpi-value">{value}</span>
      <span className="xs muted">{period}</span>
      <span className={`am-sp-kpi-change xs ${toneClass}`}>
        {ch === null ? (
          <span className="muted">no comparison</span>
        ) : (
          <>
            <strong>
              {ch.dir === 'up' ? '↑' : ch.dir === 'down' ? '↓' : '→'} {ch.pct === null ? 'new' : `${ch.pct.toFixed(ch.pct < 10 ? 2 : 1)}%`}
            </strong>
            <span className="muted"> vs. {prior}</span>
            {good !== null && <span className="visually-hidden">{good ? ' (better)' : ' (worse)'}</span>}
          </>
        )}
      </span>
      <span className="am-sp-kpi-spark">
        <Sparkline values={trend} width={200} height={24} color={good === false ? 'var(--status-critical)' : 'var(--status-good)'} />
      </span>
      <span className="am-sp-kpi-drill xs">See the tickets →</span>
    </button>
  );
}

function TicketTable({ tickets, scores }: { tickets: Ticket[]; scores?: Map<number, number> }) {
  if (!tickets.length) return <p className="muted">No tickets.</p>;
  const when = (iso: string | null) => (iso ? dayFmt(new Date(iso)) : '—');
  const sla = (t: Ticket) => (!t.resolvedDue ? '—' : !t.complete ? 'Open' : missedResolution(t) ? 'Missed' : 'Met');
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Board</th>
            <th>Assigned</th>
            <th>Status</th>
            <th className="num">Created</th>
            <th className="num">Completed</th>
            <th className="num">Hours</th>
            <th>Resolution SLA</th>
            {scores && <th className="num">Survey</th>}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => {
            const h = t.complete ? hoursToResolve(t) : NaN;
            return (
              <tr key={t.id}>
                <td>
                  <span className="mono xs">{t.ticketNumber}</span>
                  <div className="xs muted">{t.title}</div>
                </td>
                <td>
                  {t.board || '—'}
                  {t.category && <div className="xs muted">{t.category}</div>}
                </td>
                <td>{t.assignedTo ?? <span className="subtle">Unassigned</span>}</td>
                <td>
                  {t.status}
                  {t.priority && <div className="xs muted">{t.priority}</div>}
                </td>
                <td className="num">{when(t.created)}</td>
                <td className="num">{t.complete ? when(t.completed) : '—'}</td>
                <td className="num">{Number.isFinite(h) && h >= 0 ? h.toFixed(1) : '—'}</td>
                <td>{sla(t)}</td>
                {scores && <td className="num">{scores.get(t.id) ?? '—'}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- utilities */

function options(tickets: Ticket[], idKey: 'boardId' | 'categoryId', labelKey: 'board' | 'category') {
  const seen = new Map<string, string>();
  for (const t of tickets) if (t[idKey] !== null) seen.set(String(t[idKey]), t[labelKey] || `#${t[idKey]}`);
  return [...seen].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}

const share = (part: number, whole: number) => (whole ? (part / whole) * 100 : null);
const fmt = (n: number) => n.toLocaleString();
const tone = (v: number | null) => (v === null ? '' : v >= 90 ? 'good' : v >= 75 ? 'warning' : 'critical');

function readWhen(iso: string) {
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return at.toDateString() === new Date().toDateString() ? `today, ${time}` : `${dayFmt(at)}, ${time}`;
}
